import { useMutation } from "@tanstack/react-query";
import api from "@/lib/axiosConfig";
import { ImportTable } from "@/lib/import/import-types";

/**
 * Rattachement d'images en lot par n° d'enregistrement.
 *
 * L'analyse n'envoie que des **noms et des tailles** : aucun octet ne part avant
 * que l'utilisateur ait approuvé le plan. Personne ne doit téléverser 2 Go pour
 * découvrir que 100 fichiers ne correspondent à rien.
 */
const baseUrl = "back/api/data-import";

export interface ImageLinkRules {
  ignoreLeadingZeros: boolean;
  ignoreSuffixes: boolean;
  searchInName: boolean;
}

export const DEFAULT_IMAGE_LINK_RULES: ImageLinkRules = {
  ignoreLeadingZeros: true,
  ignoreSuffixes: true,
  searchInName: false,
};

export type OccupiedPolicy = "addAsAdditional" | "skip" | "replace";

export type ImageMatchState = "auto" | "ask" | "ambiguous" | "none";

export type UnlinkableReason =
  | "noMatch"
  | "unsupportedType"
  | "emptyFile"
  | "tooLarge"
  | "duplicateInSelection";

export interface ImageLinkItem {
  fileName: string;
  base: string;
  state: ImageMatchState;
  rung: number | null;
  recordId: string | null;
  recordLabel: string | null;
  candidates: Array<{ id: string; label: string; hint: string | null }>;
  position: string | null;
  order: number | null;
  positionOccupied: boolean;
  /** transformations appliquées : clés i18n (+ params) */
  appliedRules: Array<{ key: string; params?: Record<string, string | number> }>;
  reason: UnlinkableReason | null;
}

export interface ImageLinkReport {
  table: ImportTable;
  rules: ImageLinkRules;
  filesSelected: number;
  filesRejected: number;
  filesEligible: number;
  linkedAuto: number;
  linkedAutoByRung: Record<string, number>;
  needsDecision: number;
  needsDecisionByReason: {
    multipleRecords: number;
    multipleFiles: number;
    positionOccupied: number;
    approximate: number;
  };
  unlinkable: number;
  unlinkableFiles: Array<{ fileName: string; reason: UnlinkableReason }>;
  recordsTouched: number;
  duplicateRegistrationNumbersInInstance: number;
  /** compteurs pour composer la phrase d'en-tête côté client */
  headline: {
    auto: number;
    total: number;
    records: number;
    needsDecision: number;
    unlinkable: number;
  };
}

export interface AnalyseImageLinkDto {
  table: ImportTable;
  files: Array<{ name: string; size?: number; mimetype?: string }>;
  rules?: Partial<ImageLinkRules>;
  occupied?: OccupiedPolicy;
  /** position visée par défaut (hors suffixes _recto/_verso) */
  defaultPosition?: string;
  /** langue d'interface, pour les textes du rapport produits côté serveur */
  locale?: string;
}

const analyseImageLink = async ({
  table,
  ...body
}: AnalyseImageLinkDto): Promise<{
  report: ImageLinkReport;
  items: ImageLinkItem[];
}> =>
  (
    await api.post<{ report: ImageLinkReport; items: ImageLinkItem[] }>(
      `${baseUrl}/${table}/image-link/analyse`,
      body,
    )
  ).data;

export const useAnalyseImageLink = () =>
  useMutation({ mutationFn: analyseImageLink });

/** Fichiers déclarés à l'API, sans leur contenu. */
export function declareFiles(files: File[]) {
  return files.map((file) => ({
    name: file.name,
    size: file.size,
    mimetype: file.type || undefined,
  }));
}
